/**
 * Разбор ошибок мутаций в ключ перевода.
 *
 * Бекенд отдаёт код отказа в `extensions.code` (CodedError), и именно по
 * нему карточки настроек выбирают текст. Сырое `error.message` на экран не
 * попадает нигде: у платёжных провайдеров оно способно вернуть эхом часть
 * переданного секрета, а у провайдеров видео и домена это строка вида
 * «Cloudflare Alerting API error: 403» или «BUNNY_API_ERROR: 401», по
 * которой партнёру нечего чинить. Имя провайдера в тексте выбирает уже
 * карточка (см. CARD_TEXT_KEYS в StreamingUsageCard, `domainSettings.bunny.*`
 * в DomainSettingsCard и useVideoProviderTextKeys в features/videos): здесь
 * только код.
 */

interface GraphQLErrorLike {
  extensions?: Record<string, unknown> | null;
}

export interface MutationErrorLike {
  message?: string;
  graphQLErrors?: readonly (GraphQLErrorLike | null | undefined)[] | null;
}

/**
 * Короткая подробность для общего текста ошибки, та самая, что показывается
 * в скобках. HTTP-статус из сообщения важнее имени кода: партнёру и
 * поддержке «403» говорит больше, чем «CLOUDFLARE_API_ERROR» или
 * «BUNNY_API_ERROR», а сам код уже определил, какой текст показан.
 * Секретов в сообщении нет: бекенд складывает туда только код и статус.
 */
export function extractMutationErrorDetail(
  error: MutationErrorLike | null | undefined
): string {
  const message = error?.message ?? "";
  const httpStatus = message.match(/HTTP (\d{3})/)?.[1];
  if (httpStatus) {
    return httpStatus;
  }

  for (const graphQLError of error?.graphQLErrors ?? []) {
    const code = graphQLError?.extensions?.code;
    if (typeof code === "string" && code) {
      return code;
    }
  }

  return message.match(/[A-Z][A-Z0-9_]{5,}/)?.[0] ?? "";
}

/**
 * Значение дополнительного поля `extensions` первой ошибки, у которой оно
 * есть. Бекенд складывает туда то, без чего клиенту нечем продолжить: так
 * подключение Bunny по аккаунтному ключу возвращает `bunnyLibraryId` уже
 * созданной библиотеки, чтобы повтор не заводил в аккаунте вторую.
 * Секретов в extensions нет: они уходят клиенту целиком, и бекенд это
 * учитывает.
 */
export function extractMutationErrorExtension(
  error: MutationErrorLike | null | undefined,
  name: string
): string | null {
  for (const graphQLError of error?.graphQLErrors ?? []) {
    const value = graphQLError?.extensions?.[name];
    if (typeof value === "string" && value) {
      return value;
    }
  }

  return null;
}

/**
 * Ключ перевода для ошибки мутации: сначала по `extensions.code`, затем,
 * для совместимости со старыми сервисами (они бросали код текстом
 * сообщения), поиском кода подстрокой в сообщении. Ничего не нашли —
 * общий ключ карточки.
 */
export function resolveMutationErrorKey(
  error: MutationErrorLike | null | undefined,
  map: Record<string, string>,
  fallbackKey: string
): string {
  for (const graphQLError of error?.graphQLErrors ?? []) {
    const code = graphQLError?.extensions?.code;
    if (typeof code === "string" && map[code]) {
      return map[code];
    }
  }

  const message = error?.message ?? "";
  const matched = Object.keys(map).find((code) => message.includes(code));

  return matched ? map[matched] : fallbackKey;
}
